// frontend/src/pages/Admin/LinkedInPosts.tsx
// Posts LinkedIn du brief quotidien (cron smartdata daily_brief.py) :
// copier-coller vers LinkedIn, puis marquer publié.
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Page from 'pages/Page';

import { ChainlitContext, useAuth } from '@chainlit/react-client';

import { Loader } from '@/components/Loader';

// ── Types ─────────────────────────────────────────────────────────────────────

type Status = 'a_publier' | 'publie' | 'ignore';

interface Post {
  id: string;
  created_at: string;
  brief_date: string;
  recipient_name: string;
  category: string;
  fiche_number?: string;
  jurisdiction?: string;
  chamber?: string;
  decision_date?: string;
  fiche_url?: string;
  post: string;
  status: Status;
  published_at?: string | null;
  published_by?: string | null;
}

interface Stats {
  total: number;
  by_status: Record<Status, number>;
  last_brief: string | null;
  recipients: string[];
}

const LINKEDIN_MAX = 3000;

const STATUS_LABELS: Record<Status, string> = {
  a_publier: 'À publier',
  publie: 'Publié',
  ignore: 'Ignoré'
};

const STATUS_STYLES: Record<Status, string> = {
  a_publier: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  publie:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  ignore: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
};

const CATEGORY_LABELS: Record<string, string> = {
  rgpd: 'RGPD',
  jurisprudence: 'Jurisprudence',
  cjue: 'CJUE'
};

const fmtDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })
    : '—';

// ── Petits composants ─────────────────────────────────────────────────────────

const StatCard = ({
  label,
  value,
  color = 'text-foreground'
}: {
  label: string;
  value: number | string;
  color?: string;
}) => (
  <div className="bg-card border border-border rounded-lg p-4">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
  </div>
);

const FilterSelect = ({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-muted-foreground">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const PostCard = ({
  post,
  onUpdate
}: {
  post: Post;
  onUpdate: (
    id: string,
    patch: Partial<Pick<Post, 'status' | 'post'>>
  ) => Promise<void>;
}) => {
  const [text, setText] = useState(post.post);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const dirty = text !== post.post;

  useEffect(() => setText(post.post), [post.post]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Contexte non sécurisé : repli sur la sélection du textarea
      const ta = document.getElementById(
        `post-${post.id}`
      ) as HTMLTextAreaElement | null;
      ta?.select();
      document.execCommand('copy');
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const run = async (patch: Partial<Pick<Post, 'status' | 'post'>>) => {
    setSaving(true);
    try {
      await onUpdate(post.id, patch);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`px-2 py-0.5 rounded ${STATUS_STYLES[post.status]}`}>
          {STATUS_LABELS[post.status]}
        </span>
        <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground">
          {CATEGORY_LABELS[post.category] ?? post.category}
        </span>
        <span className="text-muted-foreground">
          Brief de {post.recipient_name}
        </span>
        {post.fiche_url && (
          <a
            href={post.fiche_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline ml-auto"
          >
            {[post.jurisdiction?.toUpperCase(), post.chamber, post.fiche_number]
              .filter(Boolean)
              .join(' · ') || 'Voir la fiche'}{' '}
            ↗
          </a>
        )}
      </div>

      <textarea
        id={`post-${post.id}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground font-sans leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={copy}
          className="text-sm bg-primary text-primary-foreground rounded-lg px-3 py-1.5 hover:opacity-90"
        >
          {copied ? '✓ Copié' : '📋 Copier'}
        </button>
        {dirty && (
          <button
            onClick={() => run({ post: text })}
            disabled={saving}
            className="text-sm border border-border rounded-lg px-3 py-1.5 hover:bg-muted"
          >
            Enregistrer la modification
          </button>
        )}
        {post.status !== 'publie' && (
          <button
            onClick={() =>
              run({ status: 'publie', ...(dirty ? { post: text } : {}) })
            }
            disabled={saving}
            className="text-sm border border-emerald-600 text-emerald-700 dark:text-emerald-400 rounded-lg px-3 py-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
          >
            ✓ Marquer publié
          </button>
        )}
        {post.status === 'a_publier' && (
          <button
            onClick={() => run({ status: 'ignore' })}
            disabled={saving}
            className="text-sm text-muted-foreground hover:text-foreground px-2 py-1.5"
          >
            Ignorer
          </button>
        )}
        {post.status !== 'a_publier' && (
          <button
            onClick={() => run({ status: 'a_publier' })}
            disabled={saving}
            className="text-sm text-muted-foreground hover:text-foreground px-2 py-1.5"
          >
            Remettre à publier
          </button>
        )}
        <span
          className={`ml-auto text-xs ${
            text.length > LINKEDIN_MAX
              ? 'text-rose-600'
              : 'text-muted-foreground'
          }`}
        >
          {text.length} / {LINKEDIN_MAX}
        </span>
      </div>
      {post.status === 'publie' && post.published_at && (
        <div className="text-xs text-muted-foreground">
          Publié le {new Date(post.published_at).toLocaleString('fr-FR')}
          {post.published_by ? ` par ${post.published_by}` : ''}
        </div>
      )}
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

const LinkedInPostsContent = () => {
  const { isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();

  const [stats, setStats] = useState<Stats | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('a_publier');
  const [category, setCategory] = useState('');
  const [recipient, setRecipient] = useState('');

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, isReady, navigate]);

  const loadStats = useCallback(() => {
    if (!isAuthenticated || !isReady) return;
    fetch(apiClient.buildEndpoint('/api/linkedin-posts/stats'), {
      credentials: 'include'
    })
      .then((r) => r.json())
      .then(setStats)
      .catch(() => setStats(null));
  }, [isAuthenticated, isReady, apiClient]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const load = useCallback(
    (before: string | null) => {
      if (!isAuthenticated || !isReady) return;
      setLoading(true);
      setError('');
      const p = new URLSearchParams({ limit: '20' });
      if (status) p.set('status', status);
      if (category) p.set('category', category);
      if (recipient) p.set('recipient', recipient);
      if (before) p.set('before', before);
      fetch(apiClient.buildEndpoint(`/api/linkedin-posts/?${p}`), {
        credentials: 'include'
      })
        .then((r) => {
          if (!r.ok) throw new Error(`Erreur API (${r.status})`);
          return r.json();
        })
        .then((d) => {
          setPosts((prev) => (before ? [...prev, ...d.items] : d.items));
          setTotal(d.total ?? 0);
          setCursor(d.next_cursor ?? null);
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [isAuthenticated, isReady, apiClient, status, category, recipient]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const updatePost = async (
    id: string,
    patch: Partial<Pick<Post, 'status' | 'post'>>
  ) => {
    const r = await fetch(
      apiClient.buildEndpoint(`/api/linkedin-posts/${id}`),
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      }
    );
    if (!r.ok) {
      setError(`Mise à jour impossible (${r.status})`);
      return;
    }
    const updated = await r.json();
    setPosts((prev) =>
      prev
        .map((p) => (p.id === id ? { ...p, ...updated } : p))
        // Le post quitte la vue filtrée par statut (ex. « À publier » → « Publié »)
        .filter((p) => !status || p.status === status)
    );
    loadStats();
  };

  // Regroupement par jour de brief
  const byDay = posts.reduce<Record<string, Post[]>>((acc, p) => {
    (acc[p.brief_date] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-6 p-6 max-w-screen-lg mx-auto w-full h-full overflow-y-auto [&>*]:shrink-0">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Posts LinkedIn</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Les posts du brief quotidien (7 h) : copiez-les dans LinkedIn, puis
          marquez-les publiés.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="À publier"
            value={stats.by_status.a_publier}
            color="text-blue-600"
          />
          <StatCard
            label="Publiés"
            value={stats.by_status.publie}
            color="text-emerald-600"
          />
          <StatCard label="Ignorés" value={stats.by_status.ignore} />
          <StatCard
            label="Dernier brief"
            value={stats.last_brief ? fmtDate(stats.last_brief) : '—'}
          />
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap gap-4 items-end">
        <FilterSelect
          label="Statut"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'a_publier', label: 'À publier' },
            { value: 'publie', label: 'Publiés' },
            { value: 'ignore', label: 'Ignorés' },
            { value: '', label: 'Tous' }
          ]}
        />
        <FilterSelect
          label="Catégorie"
          value={category}
          onChange={setCategory}
          options={[
            { value: '', label: 'Toutes' },
            { value: 'rgpd', label: 'RGPD' },
            { value: 'jurisprudence', label: 'Jurisprudence' },
            { value: 'cjue', label: 'CJUE' }
          ]}
        />
        <FilterSelect
          label="Brief de"
          value={recipient}
          onChange={setRecipient}
          options={[
            { value: '', label: 'Tout le monde' },
            ...(stats?.recipients ?? []).map((r) => ({ value: r, label: r }))
          ]}
        />
        <div className="text-xs text-muted-foreground ml-auto">
          {total} post{total !== 1 ? 's' : ''}
        </div>
      </div>

      {error && <div className="text-sm text-rose-600">{error}</div>}

      {Object.entries(byDay).map(([day, dayPosts]) => (
        <section key={day} className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-muted-foreground capitalize">
            {fmtDate(day)}
          </h2>
          {dayPosts.map((p) => (
            <PostCard key={p.id} post={p} onUpdate={updatePost} />
          ))}
        </section>
      ))}

      {loading && (
        <div className="flex justify-center py-6">
          <Loader />
        </div>
      )}
      {!loading && cursor && (
        <div className="flex justify-center">
          <button
            onClick={() => load(cursor)}
            className="text-sm border border-border rounded-lg px-4 py-1.5 hover:bg-muted"
          >
            Charger plus
          </button>
        </div>
      )}
      {!loading && !posts.length && !error && (
        <div className="text-center text-sm text-muted-foreground py-10">
          Aucun post pour ces filtres. Les posts arrivent avec le brief de 7 h.
        </div>
      )}
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────

const LinkedInPosts = () => (
  <Page>
    <LinkedInPostsContent />
  </Page>
);

export default LinkedInPosts;
